<script setup lang="ts">
  import { computed, watchEffect } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { Routes } from '@/const/Routes'
  import { IbexaImageVariations } from '@/enums/IbexaImageVariations'
  import type { AnchorNavigationPage } from '@/types/AnchorNavigationPage'
  import { useImage } from '@/composables/useImage'
  import { useAppRouting } from '@/composables/useAppRouting'
  import { useAuthStore } from '@/stores/auth'

  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Typography from '@/components/common/Typography.vue'
  import VgsButton from '@/components/common/VgsButton.vue'

  const props = defineProps<Partial<AnchorNavigationPage> & {
    superTitle: string
    resourceGuide?: AnchorNavigationPage
  }>()

  const { isTeacher } = useAuthStore()
  const { t } = useI18n()
  const { altText, copyright, caption, getImageByField, setImageOptions } = useImage()
  const { getRouteWithParams } = useAppRouting()

  const imageSrc = computed(() => props.image?.destinationContentId
    ? getImageByField(props.image.destinationContentId, 'image', IbexaImageVariations.ThumbnailLarge)
    : '')

  watchEffect(() => {
    if (!props.image?.destinationContentId) return
    setImageOptions({ imageId: props.image.destinationContentId })
  })
</script>

<template>
  <div :class="['wrapper', { 'no-image': !imageSrc }]">
    <div class="text-wrapper">
      <div class="text">
        <Typography
          variant="label"
          class="super-title"
        >
          {{ superTitle }}
        </Typography>
        <Typography
          v-if="title"
          variant="h1"
        >
          {{ title }}
        </Typography>
        <RichTextRenderer
          :rich-text="description"
          class="description rich-text-description"
        />
        <router-link
          v-if="isTeacher && resourceGuide"
          :to="getRouteWithParams(Routes.ResourceGuide, resourceGuide)"
          class="to-teacher-button"
        >
          <VgsButton
            v-if="isTeacher && resourceGuide"
            type="label"
            variant="quaternary"
            shape="rounded"
            icon-left="info-circle"
          >
            {{ t('subject.to-teacher') }}
          </VgsButton>
        </router-link>
      </div>
    </div>
    <div
      v-if="imageSrc"
      class="image-wrapper"
    >
      <ImageWithCaption
        :src="imageSrc"
        :alt-text="altText"
        :copyright="copyright"
        :caption="caption"
        object-fit="cover"
        class="image"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  @include color-pairs;

  .wrapper {
    background-color: var(--color-yellow-10);
    color: var(--app-theme-color);
    display: flex;

    @include horizontal-padding-mobile;

    @include vgs-media-tablet-and-desktop {
      margin: 0 auto;
      width: 100%;
    }

    @include vgs-media-mobile {
      flex-direction: column;
      margin-top: calc(16px + 2.6em);
    }
  }

  .text-wrapper {
    flex: 16;
  }

  .text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 64px 48px;
    width: 100%;

    @include vgs-media-mobile {
      padding: 64px 0 40px;
    }

    .super-title {
      margin-bottom: 4px;
    }

    .description,
    .cms-p {
      margin: 0;
    }

    .description {
      max-width: 640px;
    }
  }

  .no-image {
    max-width: 740px;
  }

  .rich-text-description {
    @include preamble;
  }

  .image-wrapper {
    flex: 15;

    @include vgs-media-mobile {
      margin-bottom: 24px;
    }
  }

  .to-teacher-button {
    box-shadow: none;
    margin-top: 16px;

    &:focus-visible {
      .ks-button {
        @include focus-ring;
      }
    }
  }

  .image {
    height: 100%;
  }
</style>
